import type React from 'react';
import { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { useGetUsersList } from 'hooks/api/user/useGetUsersList';
import { usePutUser } from 'hooks/api/user/usePutUser';
import useFormikErrors from 'hooks/formik/useFormikErrors';
import { usePagination } from 'hooks/pagination/usePagination';
import type { ProjectNames, RoleNames } from 'hooks/permission/types';
import usePermission from 'hooks/permission/usePermission';

import { prepareFilterPayload } from 'utils/common';
import { logger } from 'utils/logger';
import { ERROR_REASONS } from 'utils/logger/transports/constants';
import { openLogsInNewTab } from 'utils/navigation/navigationHelpers';

import { FeaturesTypes } from 'pages/auditLogs/types';

import { AuditLogs } from 'constants/routerPath';
import { SUCCESS_SNACKBAR_MESSAGE_PROPS } from 'constants/snackbar';

import type { PermissionsFormikValuesType } from '../components/DetailsPermissionsModal/types';
import type { UserType } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'store/AppProvider';

const SCREEN_NAME = 'users_page';

const useEnhancedPage = () => {
    const { appState } = useAppContext();
    const toolbarValues = appState.toolbar[SCREEN_NAME];

    const { paginationModel, setPaginationParams } = usePagination();
    const [totalCount, setTotalCount] = useState(0);
    const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
    const [detailModal, setDetailModal] = useState(false);
    const [modalStatus, setModalStatus] = useState(false);
    const [projectIdWithAdminRole, setProjectIdWithAdminRole] = useState<number | undefined>(undefined);
    const permissionRolePriority = appState.generalData.permissionRolePriority;

    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { showErrorMessages } = useFormikErrors();

    const projectList = appState.generalData.projectsList;

    const { data: putUser, mutate, isPending: isLoadingOnMutate, error: putUserError, isError } = usePutUser();

    useEffect(() => {
        if (putUser) {
            void queryClient.invalidateQueries({
                queryKey: ['users'],
            });
            enqueueSnackbar('User has been successfully updated', SUCCESS_SNACKBAR_MESSAGE_PROPS);
        }
    }, [putUser]);

    useEffect(() => {
        if (isError) {
            showErrorMessages({
                errors: putUserError.data,
                showGlobalMessage: true,
                customMessage: 'Error updating user',
            });
        }
        logger.error(Error('Error updating user:'), {
            cause: putUserError,
            tags: {
                reason: ERROR_REASONS.updatingUser,
                errorCode: putUserError?.status,
            },
        });
    }, [putUserError]);

    const {
        data,
        isPending: isLoading,
        error,
    } = useGetUsersList({
        searchParams: prepareFilterPayload(toolbarValues),
    });

    useEffect(() => {
        if (error) {
            showErrorMessages({
                errors: error.data,
                showGlobalMessage: true,
                customMessage: 'Error fetching users',
            });
            logger.error(Error('Error fetching users list:'), {
                cause: error,
                tags: {
                    reason: ERROR_REASONS.getUserList,
                    errorCode: error.status,
                },
            });
        }
    }, [error]);

    useEffect(() => {
        if (data) {
            setTotalCount(data.length);
        }
    }, [data]);

    const { groups } = usePermission();

    const getProjectNameFromString = (value?: string) => {
        return value as unknown as ProjectNames;
    };

    useEffect(() => {
        const project = projectList.find((item) => {
            return item.system_name === getProjectNameFromString(permissionRolePriority.projectPriority);
        });

        if (project) {
            setProjectIdWithAdminRole(project.id);
        }
    }, [groups]);

    const handleMutate = (values: PermissionsFormikValuesType) => {
        const roles: RoleNames[] = [];

        values.permission.forEach((item) => {
            item.projects?.forEach((project) => roles.push(`${project}:${item.role}` as RoleNames));
        });
        mutate({ projectId: projectIdWithAdminRole, userId: selectedUser?.aws_id ?? '', body: { groups: roles } });
    };

    const handleOpenDetailLink = (e: React.MouseEvent, user: UserType) => {
        e.stopPropagation();
        setSelectedUser(user);
        setDetailModal(true);
    };

    const handleOpenDeleteModal = (user: UserType) => {
        setSelectedUser(user);
        setModalStatus(true);
    };

    const handleDeleteAllRights = (user: UserType) => {
        setModalStatus(false);
        if (user.aws_id) {
            mutate({ projectId: projectIdWithAdminRole, userId: user.aws_id, body: { groups: [] } });
        }
    };

    const handleLogsClick = (id: number) => {
        openLogsInNewTab(AuditLogs, FeaturesTypes.users, id);
    };

    const getProjectColor = (projectName: string) => projectList.find((project) => projectName === project.system_name.toString())?.color;
    const getProjectName = (projectName: string) => projectList.find((project) => project.system_name.toString() === projectName)?.name;

    const systemNamesList = projectList.map((project) => project.system_name);

    const getCurrentUserProjectList = (user: UserType) => {
        return Object.keys(user.groups).map((item, index) => `${item}${index !== Object.keys(user.groups).length - 1 ? ', ' : ''}`);
    };

    return {
        toolbarValues,
        paginationModel,
        screenName: SCREEN_NAME,
        totalCount,
        selectedUser,
        detailModal,
        modalStatus,
        projectIdWithAdminRole,
        permissionRolePriority,
        projectList,
        data,
        isLoading,
        groups,
        systemNamesList,
        isLoadingOnMutate,
        setPaginationParams,
        getProjectNameFromString,
        setDetailModal,
        setModalStatus,
        handleMutate,
        handleOpenDetailLink,
        handleOpenDeleteModal,
        handleDeleteAllRights,
        handleLogsClick,
        getProjectColor,
        getProjectName,
        getCurrentUserProjectList,
    };
};

export { useEnhancedPage };
